import { getFlightFareFamilies, searchFlights } from 'api/flights'
import { fareClasses, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, FlightsFareTypes, FlightViewTypes } from 'constants/flight'
import { CHARTER } from 'constants/provider'
import { last, nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment'
import { EmptyBaggage, getFlightExpiry, getJourneyFields } from 'lib/checkout/flights/cart'
import { getTotalStopCount } from 'lib/flights/flightUtils'
import { multiCartRefreshFlightItemEvent } from 'analytics/snowplow/events'

/**
 * Helper function to match a fare family from upsell results.
 * For charter flights (which may have empty fareBasis), falls back to cabin matching.
 *
 * Priority:
 * 1. Match by fareBasis (exact match)
 * 2. Match by cabin (fallback for charter flights with empty fareBasis)
 * 3. Match by fareFamily.name (additional fallback)
 */
function findMatchingFareFamily(
  upsellFares: Array<App.FlightFareFamily>,
  originalFareFamily: App.FlightFareFamily | undefined,
): App.FlightFareFamily | undefined {
  if (!originalFareFamily) return undefined

  // 1. Try exact fareBasis match first (works for most flights)
  if (originalFareFamily.fareBasis) {
    const fareBasisMatch = upsellFares.find(
      upsellFare => upsellFare.fareBasis === originalFareFamily.fareBasis,
    )
    if (fareBasisMatch) return fareBasisMatch
  }

  // 2. Fallback: match by cabin (e.g., "Economy", "Business")
  // This is needed for charter flights which may have empty fareBasis
  if (originalFareFamily.cabin) {
    const cabinMatch = upsellFares.find(
      upsellFare => upsellFare.cabin?.toLowerCase() === originalFareFamily.cabin?.toLowerCase(),
    )
    if (cabinMatch) return cabinMatch
  }

  // 3. Additional fallback: match by fareFamily.name
  if (originalFareFamily.fareFamily?.name) {
    const nameMatch = upsellFares.find(
      upsellFare => upsellFare.fareFamily?.name === originalFareFamily.fareFamily?.name,
    )
    if (nameMatch) return nameMatch
  }

  // No match found - return first upsell fare as last resort (or undefined)
  return upsellFares[0]
}

export interface RefreshFlightParams {
  multiCartItem: App.MultiCartFlightItem
  /** 'charter' = no 15% markup, undefined = 15% markup */
  source?: App.FlightProviders
  currentCurrency: string
  currentRegionCode: string
  enableSabreContent?: boolean
  /**
   * Use date-based matching for charter bundle flights instead of journeyKey matching.
   * When true, matches departing flight by arrival date and returning flight by departure date.
   * Default: false (use journeyKey matching)
   */
  useCharterDateMatching?: boolean
  email?: string
}

export interface RefreshFlightResult {
  updatedMultiCartItem: App.MultiCartFlightItem
  isBundlePriced: boolean
}

/**
 * Core flight refresh logic - can be used by hooks or called directly.
 *
 * Searches for updated flight prices and returns an updated multiCartItem.
 * Does NOT update XState - caller is responsible for that.
 *
 * @throws Error if refresh fails
 */
export async function refreshMultiCartFlightItem(
  params: RefreshFlightParams,
): Promise<RefreshFlightResult> {
  const { multiCartItem, source, currentCurrency, currentRegionCode, enableSabreContent, useCharterDateMatching = false, email } = params

  const item = multiCartItem.item as App.Checkout.FlightItem
  const { flights, occupants, forceBundleId } = item
  // For charter flights with source='charter', treat as bundled (isStandalone=false)
  // to get no-markup pricing from upsell API
  const isBundlePriced = source === 'charter'
  const isStandalone = isBundlePriced ? false : !item.bundledItemIds?.length

  if (!flights?.length || !occupants) {
    throw new Error('Missing flights or occupants')
  }

  multiCartRefreshFlightItemEvent(item)

  // Determine fare type
  let fareType: string
  if (flights.length === 1) {
    fareType = FlightsFareTypes.ONE_WAY
  } else if (flights.length === 2 && flights[0].arrivalAirportCode === flights[1].departingAirportCode && flights[0].departingAirportCode === flights[1].arrivalAirportCode) {
    fareType = FlightsFareTypes.RETURN
  } else {
    fareType = FlightsFareTypes.MULTI_CITY
  }

  // Prefer selectedCabinClass (set at cart-add time, always a valid FlightsClassTypes value)
  // over fareFamily.cabin (display name like "Premium Economy" which doesn't match fareClasses).
  let fareClass = item.selectedCabinClass ?? flights[0].fareFamily?.cabin.toLowerCase()
  if (!fareClass || !fareClasses.includes(fareClass as typeof fareClasses[number])) {
    fareClass = 'economy'
  }

  const flightSearchResults = await searchFlights({
    currency: currentCurrency,
    region: currentRegionCode,
    occupancies: [occupants],
    itinerary: flights.map(flight => ({
      departureAirport: flight.departingAirportCode,
      arrivalAirport: flight.arrivalAirportCode,
      departureDate: flight.departingDate,
    })),
    fareClass,
    fareType,
    forceBundleId,
    source, // 'charter' for no markup, undefined for 15% markup
    viewType: fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' : undefined,
    email,
  }, { enableSabreContent })

  const { viewType, searchId } = flightSearchResults

  // Match fares using journey keys and paired fare IDs (default)
  // Or use date matching for charter bundle flights when useCharterDateMatching is true
  const fares = nonNullable(flights.reduce<Array<App.JourneyV2 | undefined>>((acc, flight, i) => {
    const onewayFares = flightSearchResults.onewayFares[i] ?? []

    if (useCharterDateMatching) {
      // Charter date matching: match by date instead of journeyKey
      // Also match by provider to ensure we find the same airline (e.g., charter vs SQ)
      // Departing flight (i=0): match by arrival date + provider
      // Returning flight (i=1): match by departure date + paired fare ID
      const originalProvider = flight.provider
      if (i === 0) {
        // Let moment auto-parse the date format
        const targetArrivalDate = flight.arrivalDateTime
          ? moment(flight.arrivalDateTime).format('YYYY-MM-DD')
          : undefined

        // First try to match by date AND provider (preferred)
        let matchedFare = onewayFares.find(fare => {
          const arrivalDate = last(fare.flightGroup.flights)?.arrivalDate
          return arrivalDate && arrivalDate === targetArrivalDate && fare.provider === originalProvider
        })

        // Fallback: match by date only if provider match fails
        if (!matchedFare) {
          matchedFare = onewayFares.find(fare => {
            const arrivalDate = last(fare.flightGroup.flights)?.arrivalDate
            return arrivalDate && arrivalDate === targetArrivalDate
          })
        }

        acc.push(matchedFare)
      } else {
        const targetDepartDate = flight.departingDate
        const previousSegmentPairedFareIds = acc[i - 1]?.pairedFareId ?? []

        // First try to match by date, pairedId AND provider (preferred)
        let matchedFare = onewayFares.find(fare => {
          const departingDate = fare.flightGroup.flights[0]?.departingDate
          const isPaired = previousSegmentPairedFareIds.includes(fare.bookingInfo.boookingKey)
          return departingDate === targetDepartDate && isPaired && fare.provider === originalProvider
        })

        // Fallback: match by date and pairedId only if provider match fails
        if (!matchedFare) {
          matchedFare = onewayFares.find(fare => {
            const departingDate = fare.flightGroup.flights[0]?.departingDate
            const isPaired = previousSegmentPairedFareIds.includes(fare.bookingInfo.boookingKey)
            return departingDate === targetDepartDate && isPaired
          })
        }

        acc.push(matchedFare)
      }
    } else {
      // Default: match by journeyKey
      if (i === 0) {
        const firstSegmentFare = onewayFares.find(fare => fare.bookingInfo.journeyKey === flight.journeyKey)
        acc.push(firstSegmentFare)
      } else {
        const previousSegmentPairedFareIds = acc[i - 1]?.pairedFareId ?? []
        const pairedFare = onewayFares.find(fare => fare.bookingInfo.journeyKey === flight.journeyKey && previousSegmentPairedFareIds.includes(fare.bookingInfo.boookingKey))
        acc.push(pairedFare)
      }
    }
    return acc
  }, []))

  if (fares.length !== flights.length) {
    multiCartRefreshFlightItemEvent(item, 'failed')
    throw new Error('Could not match all flight fares')
  }

  // Get upsell options based on view type
  // For charter flights, we MUST call the upsell API to get valid fareFamily IDs for the new search session.
  // The select endpoint requires fareFamily IDs that match the current searchId.
  let upsells: Array<App.FlightFareFamily | undefined> = []

  if (viewType === 'TwoOneWays') {
    let departingUpsell: App.FlightFareFamily | undefined
    let returningUpsell: App.FlightFareFamily | undefined

    if (flights[0].fareFamily) {
      const departingUpsellResults = await getFlightFareFamilies({
        searchId: searchId!,
        sessionId: '',
        origin: flights[0].departingAirportCode,
        destination: flights[0].arrivalAirportCode,
        occupancies: occupants,
        region: currentRegionCode,
        isStandalone,
        selectedFareIds: [fares[0].id],
      })
      departingUpsell = findMatchingFareFamily(departingUpsellResults.upsellFares, flights[0].fareFamily)
    }

    if (flights[1].fareFamily) {
      const returningUpsellResults = await getFlightFareFamilies({
        searchId: searchId!,
        sessionId: '',
        origin: flights[1].departingAirportCode,
        destination: flights[1].arrivalAirportCode,
        occupancies: occupants,
        region: currentRegionCode,
        departingUpsellOptionId: departingUpsell?.id,
        isStandalone,
        selectedFareIds: [fares[0].id, fares[1].id],
      })
      returningUpsell = findMatchingFareFamily(returningUpsellResults.upsellFares, flights[1].fareFamily)
    }

    upsells = [departingUpsell, returningUpsell]
  } else if (viewType === 'ReturnTwoOneWays') {
    const lastFlight = last(flights)
    if (lastFlight.fareFamily) {
      const upsellResults = await getFlightFareFamilies({
        searchId: searchId!,
        sessionId: '',
        origin: lastFlight.departingAirportCode,
        destination: lastFlight.arrivalAirportCode,
        occupancies: occupants,
        region: currentRegionCode,
        isStandalone,
        selectedFareIds: fares.map(fare => fare.id),
      })
      upsells = flights.map((originalFlight, i) => {
        const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
        return findMatchingFareFamily(upsellFares, originalFlight.fareFamily)
      })
    }
  } else if (viewType === 'MultiCity' || viewType === 'OneWay') {
    upsells = await Promise.all(flights.map(async(originalFlight, i) => {
      if (originalFlight.fareFamily) {
        const upsellResults = await getFlightFareFamilies({
          searchId: searchId!,
          sessionId: '',
          origin: originalFlight.departingAirportCode,
          destination: originalFlight.arrivalAirportCode,
          occupancies: occupants,
          region: currentRegionCode,
          isStandalone,
          selectedFareIds: fares.map(fare => fare.id),
        })
        const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
        return findMatchingFareFamily(upsellFares, originalFlight.fareFamily)
      }
      return undefined
    }))
  } else {
    multiCartRefreshFlightItemEvent(item, 'failed')
    throw new Error(`Unsupported viewType: ${viewType}`)
  }

  const searchExpiry = moment(flightSearchResults.timestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()
  const expiry = getFlightExpiry(fares[0], searchExpiry)

  const updatedItem: App.Checkout.FlightItem = {
    ...item,
    expiry,
    hasFinalFare: false,
    fareFetchError: undefined,
    journeyType: fares[0].itemType,
    forceBundleId,
    totalFare: 0,
    searchId,
    viewType: fares[0].fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : item.viewType,
    flights: flights.map((flight, i) => {
      const journey = fares[i]
      const seatEligiblePassengers = item.passengers.filter(passenger => passenger.type !== 'infant').length
      const journeyFields = getJourneyFields(journey, seatEligiblePassengers)
      const upsellFareFamily = upsells[i]

      // Use upsell fareFamily directly - when isStandalone=false is passed to upsell API,
      // it returns correct bundle pricing (no markup for charter flights)

      return {
        ...journeyFields,
        carrier: journey.flightGroup.flights[0].marketingAirline.code,
        extras: flight.extras ?? EmptyBaggage,
        fareFamily: upsellFareFamily,
        departingAirportCode: journey.flightGroup.flights[0].departureAirport,
        arrivalAirportCode: last(journey.flightGroup.flights).arrivalAirport,
        departingDate: journey.flightGroup.flights[0].departingDate,
        arrivalAirportName: last(journey.flightGroup.flights).arrivalAirportName,
        departingAirportName: journey.flightGroup.flights[0].departureAirportName,
        arrivalDateTime: last(journey.flightGroup.flights).arrivalDateTime,
        departureDateTime: journey.flightGroup.flights[0].departureDateTime,
        duration: journey.flightGroup.totalFlightDuration,
        numberOfStops: getTotalStopCount(journey.flightGroup.flights),
      }
    }),
  }

  const updatedMultiCartItem: App.MultiCartFlightItem = {
    ...multiCartItem,
    item: updatedItem,
    state: 'active',
    expiry: updatedItem.expiry,
    isBundlePriced,
    flights: updatedItem.flights.map(flight => ({
      journeyId: flight.journeyId,
      journeyKey: flight.journeyKey,
      extras: flight.extras,
      provider: flight.provider || '',
      providerSearchId: flight.providerSearchId || '',
      fareFamily: flight.fareFamily,
      withEmbeddedInsurance: flight.withEmbeddedInsurance,
      cost: flight.cost,
      carrier: flight.carrier,
      carrierImage: flight.carrierImage,
      departingAirportCode: flight.departingAirportCode,
      departingAirportName: flight.departingAirportName,
      arrivalAirportCode: flight.arrivalAirportCode,
      arrivalAirportName: flight.arrivalAirportName,
      departingDate: flight.departingDate,
      arrivalDateTime: flight.arrivalDateTime,
      departureDateTime: flight.departureDateTime,
      duration: flight.duration,
      numberOfStops: flight.numberOfStops,
    })),
    searchId: updatedItem.searchId,
    hasFinalFare: updatedItem.hasFinalFare,
    viewType: updatedItem.viewType,
  }

  multiCartRefreshFlightItemEvent(item, 'success')

  return {
    updatedMultiCartItem,
    isBundlePriced,
  }
}

export interface DualCharterPricingParams {
  multiCartItem: App.MultiCartFlightItem
  currentCurrency: string
  currentRegionCode: string
  enableSabreContent?: boolean
  email?: string
}

export interface DualCharterPricingResult {
  /** Updated cart item with bundle pricing applied */
  bundleMultiCartItem: App.MultiCartFlightItem
  /** Updated cart item with standalone pricing applied */
  standaloneMultiCartItem: App.MultiCartFlightItem
  /** Bundle pricing data (no 15% markup) for Redux cache */
  bundlePricing: App.CharterFlightPricingData
  /** Standalone pricing data (with 15% markup) for Redux cache */
  standalonePricing: App.CharterFlightPricingData
}

/**
 * Fetches both bundle (no 15% markup) and standalone (with 15% markup) pricing
 * for a charter flight in parallel.
 *
 * Returns both pricing data objects for Redux storage and both updated cart items.
 * Caller is responsible for:
 * 1. Storing pricing in Redux via setCharterFlightDualPricing action
 * 2. Updating XState with the appropriate cart item
 *
 * @throws Error if either fetch fails
 */
export async function fetchDualCharterPricing(
  params: DualCharterPricingParams,
): Promise<DualCharterPricingResult> {
  const { multiCartItem, currentCurrency, currentRegionCode, enableSabreContent, email } = params

  // Fetch both bundle and standalone prices in parallel
  const [bundleResult, standaloneResult] = await Promise.all([
    refreshMultiCartFlightItem({
      multiCartItem,
      source: CHARTER, // No 15% markup
      currentCurrency,
      currentRegionCode,
      enableSabreContent,
      useCharterDateMatching: true,
      email,
    }),
    refreshMultiCartFlightItem({
      multiCartItem,
      source: undefined, // With 15% markup
      currentCurrency,
      currentRegionCode,
      enableSabreContent,
      useCharterDateMatching: true,
      email,
    }),
  ])

  const bundleItem = bundleResult.updatedMultiCartItem
  const standaloneItem = standaloneResult.updatedMultiCartItem

  // Build pricing cache objects for Redux
  const bundlePricing: App.CharterFlightPricingData = {
    flights: bundleItem.flights,
    searchId: bundleItem.searchId,
    expiry: bundleItem.expiry,
    viewType: bundleItem.viewType,
    hasFinalFare: bundleItem.hasFinalFare,
  }

  const standalonePricing: App.CharterFlightPricingData = {
    flights: standaloneItem.flights,
    searchId: standaloneItem.searchId,
    expiry: standaloneItem.expiry,
    viewType: standaloneItem.viewType,
    hasFinalFare: standaloneItem.hasFinalFare,
  }

  return {
    bundleMultiCartItem: bundleItem,
    standaloneMultiCartItem: standaloneItem,
    bundlePricing,
    standalonePricing,
  }
}

/**
 * Applies cached pricing data to a charter flight cart item.
 * Used for instant switching between bundle and standalone pricing.
 *
 * @param multiCartItem - The flight cart item to update
 * @param pricingData - Cached pricing data from Redux
 * @param useBundlePricing - Whether this is bundle pricing (affects isBundlePriced flag)
 * @returns Updated MultiCartFlightItem with the pricing applied
 */
export function applyCharterPricing(
  multiCartItem: App.MultiCartFlightItem,
  pricingData: App.CharterFlightPricingData,
  useBundlePricing: boolean,
): App.MultiCartFlightItem {
  const item = multiCartItem.item as App.Checkout.FlightItem

  // Update the checkout item's flights with the cached pricing
  const updatedItem: App.Checkout.FlightItem = {
    ...item,
    searchId: pricingData.searchId,
    expiry: pricingData.expiry,
    viewType: pricingData.viewType,
    hasFinalFare: pricingData.hasFinalFare,
    flights: item.flights.map((flight, i) => {
      const cachedFlight = pricingData.flights[i]
      if (!cachedFlight) return flight

      return {
        ...flight,
        journeyId: cachedFlight.journeyId,
        journeyKey: cachedFlight.journeyKey,
        fareFamily: cachedFlight.fareFamily,
        cost: cachedFlight.cost,
        provider: cachedFlight.provider,
        providerSearchId: cachedFlight.providerSearchId,
      }
    }),
  }

  return {
    ...multiCartItem,
    item: updatedItem,
    isBundlePriced: useBundlePricing,
    flights: pricingData.flights,
    searchId: pricingData.searchId,
    expiry: pricingData.expiry,
    viewType: pricingData.viewType,
    hasFinalFare: pricingData.hasFinalFare,
  }
}
